import { forwardRef, useMemo } from 'react'
import Leaflet from 'leaflet'
import { CircleMarker, MapContainer, Popup, TileLayer } from 'react-leaflet'

import { Box, Button, Stack, Typography } from '@mui/material'

import { SisetusDpcResult } from 'interface/dpc/types'
import { SelectedDpc } from '../types'

type SisetuPopupProps = {
  result: SisetusDpcResult
  selectedDpcList: SelectedDpc[]
  onOpen: (sisetuCode: string) => void
  onClose: () => void
}
const SisetuPopup = ({
  result: {
    sisetu,
    surgery,
    treatment1,
    treatment2
  },
  selectedDpcList,
  onOpen,
  onClose
}: SisetuPopupProps) => {
  return (
    <Popup
      eventHandlers={{
        remove: onClose,
      }}
    >
      <Stack spacing={1}>
        <Box>
          <Typography variant="body1" component="div" color="text.primary">
            {sisetu.sisetuName}
          </Typography>
          <Typography variant="body2" component="div" color="text.secondary">
            {sisetu.juusyo}
          </Typography>
          <Typography variant="body2" component="div" color="text.secondary">
            {sisetu.tel}
          </Typography>
        </Box>
        <Stack>
          {selectedDpcList.map((dpc, index) => {
            return (
              <Stack
                key={`${dpc.dpcCode}-count-${index}`}
                direction="column"
                spacing={0.5}
              >
                <Typography variant="body1" component="div" color="text.primary">{dpc.diseaseName}</Typography>
                <Typography variant="body1" component="div" color="text.primary">手術別 : {surgery[dpc.dpcCode]?.totalCases ?? '-'} 件</Typography>
                <Typography variant="body1" component="div" color="text.primary">処置(1) : {treatment1[dpc.dpcCode]?.totalCases ?? '-'} 件</Typography>
                <Typography variant="body1" component="div" color="text.primary">処置(2) : {treatment2[dpc.dpcCode]?.totalCases ?? '-'} 件</Typography>
              </Stack>
            )
          })}
        </Stack>

        <Button
          fullWidth
          variant="outlined"
          onClick={() => {
            if (sisetu.sisetuCode) onOpen(sisetu.sisetuCode)
          }}
          size="small"
        >
          開く
        </Button>
      </Stack>
    </Popup>
  )
}

type FacilityMapViewProps = {
  results: SisetusDpcResult[]
  selectedDpcList: SelectedDpc[]
  calcTotalCase: (result: SisetusDpcResult, dpcCode: string) => number | undefined
  onOpen: (sisetuCode: string) => void
  onClose: () => void
}
export const FacilityMapView = forwardRef<Leaflet.Map, FacilityMapViewProps>(({
  results,
  selectedDpcList,
  calcTotalCase,
  onOpen,
  onClose
}, ref) => {
  const { data, maxCases } = useMemo(
    () => {
      let maxCases = 0
      const data = results.flatMap(result => {
        if (!result.sisetu.location) return []
        const { latitude, longitude } = result.sisetu.location
        if (!latitude || !longitude) return []
        return {
          result, latitude, longitude,
          results: selectedDpcList.flatMap(dpc => {
            const totalCases = calcTotalCase(result, dpc.dpcCode)
            if (!totalCases) return []
            maxCases = Math.max(maxCases, totalCases)
            return {
              dpcCode: dpc.dpcCode,
              fillColor: dpc.color,
              totalCases
            }
          })
        }
      })
      return { data, maxCases }
    },
    [results, selectedDpcList, calcTotalCase]
  )
  return (
    <MapContainer
      ref={ref}
      center={[35.686991, 139.539242]}
      zoom={5}
      scrollWheelZoom={true}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {data.map(({ result, latitude, longitude, results }, index) => results.map(({ dpcCode, fillColor, totalCases }) => (
        <CircleMarker
          key={`${result.sisetu.sisetuCode}-${index}-${dpcCode}`}
          center={[latitude, longitude]}
          pathOptions={{ fillColor }}
          stroke={false}
          radius={((totalCases * 50) / maxCases) + 5}
          fillOpacity={0.5}
        >
          <SisetuPopup
            result={result}
            selectedDpcList={selectedDpcList}
            onOpen={onOpen}
            onClose={onClose}
          />
        </CircleMarker>
      )))}
    </MapContainer>
  )
})

FacilityMapView.displayName = 'FacilityMapView'
